import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../constants';


const RegistrationManagement = () => {
  // State for registrations data
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // State for filters and search
  const [activeTab, setActiveTab] = useState('Confirmed');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [updateData, setUpdateData] = useState({
    hotelName: '',
    hotelLocation: '',
    roomNumber: '',
    hasFamilyMember: false,
    familyMemberRelation: '',
    familyMemberName: '',
    otherRelation: ''
  });
  const [modalErrors, setModalErrors] = useState({});
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // Fetch registrations data
  useEffect(() => {
    fetchRegistrations();
  }, []);
  
  // Apply filters when tab changes or search term changes
  useEffect(() => {
    applyFilters();
  }, [activeTab, searchTerm, registrations]);
  
  const fetchRegistrations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${BASE_URL}/admin/travel-registrations`);
      
      if (response.data.status === "200") {
        setRegistrations(response.data.data);
      } else {
        setError('Failed to fetch registrations data');
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setError('An error occurred while fetching registrations');
    } finally {
      setLoading(false);
    }
  };
  
  const applyFilters = () => {
    let filtered = [...registrations];
    
    // Apply status filter
    filtered = filtered.filter(reg => reg.status === activeTab);
    
    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(reg => 
        reg.partnerName.toLowerCase().includes(term) || 
        reg.mobileNumber.includes(term) ||
        reg.registrationNumber.includes(term)
      );
    }
    
    // Apply pagination
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setFilteredRegistrations(filtered);
    
    // Reset to first page when filters change
    setCurrentPage(1);
  };
  
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRegistrations.slice(startIndex, endIndex);
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handleOpenModal = (registration) => {
    setSelectedRegistration(registration);
    setUpdateData({
      hotelName: registration.hotelName || '',
      hotelLocation: registration.hotelLocation || '',
      roomNumber: registration.roomNumber || '',
      hasFamilyMember: !!registration.familyMemberRelation,
      familyMemberRelation: registration.familyMemberRelation || '',
      familyMemberName: registration.familyMemberName || '',
      otherRelation: registration.familyMemberRelation === 'Other' ? registration.otherRelation || '' : ''
    });
    setModalErrors({});
    setUpdateSuccess(false);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRegistration(null);
  };
  
  const handleUpdateChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox fields
    if (type === 'checkbox') {
      setUpdateData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setUpdateData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear errors when field is updated
    if (modalErrors[name]) {
      setModalErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleUpdate = async () => {
    // Validate inputs
    const errors = {};
    
    if (!updateData.hotelName.trim()) {
      errors.hotelName = 'Hotel name is required';
    }
    
    if (!updateData.hotelLocation.trim()) {
      errors.hotelLocation = 'Hotel location is required';
    }
    
    if (!updateData.roomNumber.trim()) {
      errors.roomNumber = 'Room number is required';
    }
    
    if (updateData.hasFamilyMember) {
      if (!updateData.familyMemberRelation) {
        errors.familyMemberRelation = 'Please select a relation';
      }
      
      if (!updateData.familyMemberName.trim()) {
        errors.familyMemberName = 'Family member name is required';
      }
      
      if (updateData.familyMemberRelation === 'Other' && !updateData.otherRelation.trim()) {
        errors.otherRelation = 'Please specify the relation';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setModalErrors(errors);
      return;
    }
    
    // Prepare update data for API
    const updatedData = {
      hotelName: updateData.hotelName,
      hotelLocation: updateData.hotelLocation,
      roomNumber: updateData.roomNumber,
      familyMemberRelation: updateData.hasFamilyMember ? updateData.familyMemberRelation : null,
      familyMemberName: updateData.hasFamilyMember ? updateData.familyMemberName : null,
      otherRelation: updateData.familyMemberRelation === 'Other' ? updateData.otherRelation : null
    };
    
    try {
      // Make API call to update registration
      const response = await axios.put(
        `${BASE_URL}/admin/travel-registrations/${selectedRegistration.id}/update-accommodation`,
        updatedData
      );
      console.log();
      

      
      if (response.data.status === "200") {
        setUpdateSuccess(true);
        
        // Update the registrations list with new data
        setRegistrations(prevRegs => prevRegs.map(reg => 
          reg.id === selectedRegistration.id ? {...reg, ...updatedData} : reg
        ));
        
        // Auto close modal after 2 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setModalErrors({ api: response.data.message || 'Update failed' });
      }
    } catch (error) {
      console.error('Error updating registration:', error);
      setModalErrors({ api: 'An error occurred while updating accommodation details' });
    }
  };
  
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-600 px-4 py-6 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-white">Poultry Conclave Registration Management</h1>
          <p className="mt-1 text-sm text-green-100">Manage participant registrations and accommodation details</p>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Status tabs and search bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => handleTabChange('Confirmed')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'Confirmed'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Confirmed
              <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                {registrations.filter(r => r.status === 'Confirmed').length}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('Pending')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'Pending'
                  ? 'border-b-2 border-yellow-600 text-yellow-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Pending
              <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                {registrations.filter(r => r.status === 'Pending').length}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('Cancelled')}
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'Cancelled'
                  ? 'border-b-2 border-red-600 text-red-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Cancelled
              <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                {registrations.filter(r => r.status === 'Cancelled').length}
              </span>
            </button>
          </div>
          
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name, mobile or registration #"
              value={searchTerm}
              onChange={handleSearch}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
          </div>
        </div>
        
        {/* Data table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="py-20 text-center">
                <svg className="animate-spin h-10 w-10 text-green-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-2 text-gray-500">Loading registrations...</p>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <div className="mx-auto h-12 w-12 text-red-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="mt-2 text-red-500">{error}</p>
                <button 
                  onClick={fetchRegistrations}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Try Again
                </button>
              </div>
            ) : filteredRegistrations.length === 0 ? (
              <div className="py-20 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="mt-2 text-gray-500">No registrations found</p>
                {searchTerm && (
                  <p className="text-sm text-gray-400">Try adjusting your search criteria</p>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Participant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile / Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Travel Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accommodation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getPaginatedData().map((registration) => (
                    <tr key={registration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">{registration.registrationNumber}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{registration.partnerName}</div>
                        <div className="text-sm text-gray-500">
                          Traveler: {registration.travelerName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{registration.mobileNumber}</div>
                        <div className="text-sm text-gray-500">{registration.partnerCode}</div>
                        <div className="text-xs text-gray-500">{registration.location}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            registration.travelMode === 'Air' ? 'bg-blue-100 text-blue-800' :
                            registration.travelMode === 'Train' ? 'bg-purple-100 text-purple-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {registration.travelMode}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {formatDate(registration.expectedArrivalDateTime)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Alt. No: {registration.alternateMobile}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {registration.hotelName ? (
                          <>
                            <div className="text-sm font-medium text-gray-900">{registration.hotelName}</div>
                            <div className="text-sm text-gray-500">{registration.hotelLocation}</div>
                            <div className="text-sm text-gray-500">Room: {registration.roomNumber}</div>
                            {registration.familyMemberName && (
                              <div className="text-xs mt-1 text-gray-500">
                                With: {registration.familyMemberName} 
                                <span className="text-gray-400">
                                  ({registration.familyMemberRelation === 'Other' ? registration.otherRelation : registration.familyMemberRelation})
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Not Assigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(registration.registrationDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(registration)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Pagination */}
          {!loading && !error && filteredRegistrations.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                    currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                    currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredRegistrations.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredRegistrations.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                        currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Page buttons - show limited pages with ellipsis when needed */}
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, and pages around current page
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              currentPage === pageNumber
                                ? 'z-10 bg-green-50 border-green-500 text-green-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } text-sm font-medium`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      // Show ellipsis when needed
                      if (
                        (pageNumber === 2 && currentPage > 3) ||
                        (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                        currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal for updating accommodation details */}
      {isModalOpen && selectedRegistration && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Update Accommodation Details
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Update hotel and room information for {selectedRegistration.partnerName}
                      </p>
                      
                      {updateSuccess && (
                        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-green-800">
                                Accommodation details updated successfully!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {modalErrors.api && (
                        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-red-800">
                                {modalErrors.api}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-4">
                        {/* Hotel Name */}
                        <div>
                          <label htmlFor="hotelName" className="block text-sm font-medium text-gray-700">
                            Hotel Name*
                          </label>
                          <input
                            type="text"
                            id="hotelName"
                            name="hotelName"
                            value={updateData.hotelName}
                            onChange={handleUpdateChange}
                            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                              modalErrors.hotelName ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {modalErrors.hotelName && (
                            <p className="mt-1 text-sm text-red-600">{modalErrors.hotelName}</p>
                          )}
                        </div>
                        
                        {/* Hotel Location */}
                        <div>
                          <label htmlFor="hotelLocation" className="block text-sm font-medium text-gray-700">
                            Hotel Location*
                          </label>
                          <input
                            type="text"
                            id="hotelLocation"
                            name="hotelLocation"
                            value={updateData.hotelLocation}
                            onChange={handleUpdateChange}
                            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                              modalErrors.hotelLocation ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {modalErrors.hotelLocation && (
                            <p className="mt-1 text-sm text-red-600">{modalErrors.hotelLocation}</p>
                          )}
                        </div>
                        
                        {/* Room Number */}
                        <div>
                          <label htmlFor="roomNumber" className="block text-sm font-medium text-gray-700">
                            Room Number*
                          </label>
                          <input
                            type="text"
                            id="roomNumber"
                            name="roomNumber"
                            value={updateData.roomNumber}
                            onChange={handleUpdateChange}
                            className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                              modalErrors.roomNumber ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {modalErrors.roomNumber && (
                            <p className="mt-1 text-sm text-red-600">{modalErrors.roomNumber}</p>
                          )}
                        </div>
                        
                        {/* Family Member Toggle */}
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="hasFamilyMember"
                              name="hasFamilyMember"
                              type="checkbox"
                              checked={updateData.hasFamilyMember}
                              onChange={handleUpdateChange}
                              className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="hasFamilyMember" className="font-medium text-gray-700">
                              Accompanying Family Member
                            </label>
                            <p className="text-gray-500">Add details if a family member is joining</p>
                          </div>
                        </div>
                        
                        {/* Family Member Details - Only show when toggled on */}
                        {updateData.hasFamilyMember && (
                          <div className="space-y-4 pl-4 border-l-2 border-green-100">
                            {/* Relation */}
                            <div>
                              <label htmlFor="familyMemberRelation" className="block text-sm font-medium text-gray-700">
                                Relation*
                              </label>
                              <select
                                id="familyMemberRelation"
                                name="familyMemberRelation"
                                value={updateData.familyMemberRelation}
                                onChange={handleUpdateChange}
                                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                                  modalErrors.familyMemberRelation ? 'border-red-300' : 'border-gray-300'
                                }`}
                              >
                                <option value="">Select relation</option>
                                <option value="Spouse">Spouse (Wife/Husband)</option>
                                <option value="Brother">Brother</option>
                                <option value="Sister">Sister</option>
                                <option value="Son">Son</option>
                                <option value="Daughter">Daughter</option>
                                <option value="Father">Father</option>
                                <option value="Mother">Mother</option>
                                <option value="Other">Other</option>
                              </select>
                              {modalErrors.familyMemberRelation && (
                                <p className="mt-1 text-sm text-red-600">{modalErrors.familyMemberRelation}</p>
                              )}
                            </div>
                            
                            {/* Other Relation - Only show when "Other" is selected */}
                            {updateData.familyMemberRelation === 'Other' && (
                              <div>
                                <label htmlFor="otherRelation" className="block text-sm font-medium text-gray-700">
                                  Specify Relation*
                                </label>
                                <input
                                  type="text"
                                  id="otherRelation"
                                  name="otherRelation"
                                  value={updateData.otherRelation}
                                  onChange={handleUpdateChange}
                                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                                    modalErrors.otherRelation ? 'border-red-300' : 'border-gray-300'
                                  }`}
                                />
                                {modalErrors.otherRelation && (
                                  <p className="mt-1 text-sm text-red-600">{modalErrors.otherRelation}</p>
                                )}
                              </div>
                            )}
                            
                            {/* Name */}
                            <div>
                              <label htmlFor="familyMemberName" className="block text-sm font-medium text-gray-700">
                                Family Member Name*
                              </label>
                              <input
                                type="text"
                                id="familyMemberName"
                                name="familyMemberName"
                                value={updateData.familyMemberName}
                                onChange={handleUpdateChange}
                                className={`mt-1 block w-full rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm ${
                                  modalErrors.familyMemberName ? 'border-red-300' : 'border-gray-300'
                                }`}
                              />
                              {modalErrors.familyMemberName && (
                                <p className="mt-1 text-sm text-red-600">{modalErrors.familyMemberName}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationManagement;